import React from "react";
import {
  Collapse,
  Divider,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Box,
  Fade,
  Zoom,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore,
} from "@mui/icons-material";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { drawerWidth } from "../appbar/appbar";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { DynamicIcon } from "../../../libs/helpers";
import { ModuleI } from "../../../libs/interfaces";
import { Button as ButtonAntd } from "antd";
import { useRouterStore } from "../../../libs/stores";
import { usePlanFeatures } from "@apis";
import {
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  COLOR_TERTIARY,
  NAVBAR_HEIGHT,
} from "../../../libs/constants";
import { useWidth } from "../../../libs/hooks";
import { HomeOutlined } from "@ant-design/icons";
import "../../css/drawer.css";

// Prefijo de ruta → feature del plan requerida para ver ese ítem de menú.
// Solo se ocultan ítems cuyo feature NO está en el plan; el resto siempre se ve.
// POS (/stockify/sales) e Inventario (/stockify/inventory) NO se filtran.
const FEATURE_BY_PATH: { prefix: string; feature: string }[] = [
  { prefix: "/sri/credit-notes", feature: "NOTA_CREDITO" },
  { prefix: "/sri/debit-notes", feature: "NOTA_DEBITO" },
  { prefix: "/stockify/quotation", feature: "COTIZACION" },
  { prefix: "/stockify/orders", feature: "COMPRAS" },
  { prefix: "/stockify/suppliers", feature: "COMPRAS" },
  { prefix: "/sri/retentions", feature: "RETENCIONES" },
  { prefix: "/sri/remission-guides", feature: "GUIA_REMISION" },
  { prefix: "/finanzas/expenses", feature: "GASTOS" },
  { prefix: "/finanzas/account-payable", feature: "CUENTAS_COBRAR_PAGAR" },
  { prefix: "/finanzas/account-receivable", feature: "CUENTAS_COBRAR_PAGAR" },
  { prefix: "/finanzas/cash-register", feature: "BANCOS_CAJA" },
  { prefix: "/stockify/recurrence", feature: "RECURRENCIAS" },
  { prefix: "/stockify/import", feature: "IMPORTACION_MASIVA" },
];

const featureForPath = (to?: string): string | null => {
  if (!to) return null;
  const hit = FEATURE_BY_PATH.find((m) => to.startsWith(m.prefix));
  return hit ? hit.feature : null;
};


/* ── Tooltip config ───────────────────────────────────────────────────────── */
const propsTooltip = {
  tooltip: {
    sx: {
      background: "#1D9E75",
      color: "#ffffff",
      fontSize: "11px",
      fontWeight: 600,
      borderRadius: "6px",
      px: 1.2,
      py: 0.5,
    },
  },
  arrow: { sx: { color: "#1D9E75" } },
};

/* ── Styled Drawer ────────────────────────────────────────────────────────── */
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: "79px",
  [theme.breakpoints.up("sm")]: { width: "79px" },
});

const DrawerEl = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  zIndex: 998,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: "all .22s ease-in-out",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export const DrawerHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  padding: 0,
  justifyContent: "center",
  // Espaciador bajo el AppBar fijo: debe igualar su alto real (57px + notch).
  height: "calc(57px + env(safe-area-inset-top, 0px))",
  minHeight: "calc(57px + env(safe-area-inset-top, 0px))",
  maxHeight: "calc(57px + env(safe-area-inset-top, 0px))",
  backgroundColor: COLOR_PRIMARY,
  color: "white",
}));

/* ── Context ──────────────────────────────────────────────────────────────── */
const DrawerStateContext = React.createContext<{
  expandedModules: Set<number>;
  toggleModule: (id: number) => void;
  isModuleExpanded: (id: number) => boolean;
  expandingModule: number | null;
}>({
  expandedModules: new Set(),
  toggleModule: () => {},
  isModuleExpanded: () => false,
  expandingModule: null,
});

interface PropsDrawerI {
  open: boolean;
  handleClose: () => void;
}
interface PropsI {
  data: ModuleI;
  padding?: number;
  open: boolean;
}

/* ── Drawer principal ─────────────────────────────────────────────────────── */
export const Drawer = ({ open, handleClose }: PropsDrawerI) => {
  const width    = useWidth();
  const theme    = useTheme();
  const navigate = useNavigate();
  const modules  = useRouterStore((s) => s.modules);
  const location = useLocation();
  const { hasSubscription, features } = usePlanFeatures();

  // Oculta del menú los módulos/opciones cuyo feature no está en el plan.
  // Para SUPERADMIN (sin suscripción) no se filtra nada.
  const visibleModules = React.useMemo(() => {
    if (!hasSubscription) return modules;
    const allowed = (to?: string) => {
      const feat = featureForPath(to);
      return !feat || features.includes(feat);
    };
    return modules
      .map((m: any) => {
        if (!m.children?.length) return allowed(m.to) ? m : null;
        const children = m.children.filter((c: any) => allowed(c.to));
        return children.length ? { ...m, children } : null;
      })
      .filter(Boolean) as typeof modules;
  }, [modules, hasSubscription, features]);

  const [expandedModules, setExpandedModules] = React.useState<Set<number>>(
    () => {
      const saved = localStorage.getItem("drawer-expanded-modules");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    }
  );
  const [expandingModule, setExpandingModule] = React.useState<number | null>(null);

  const initializeActiveModules = React.useCallback(() => {
    if (!modules.length) return;
    const ids = modules
      .filter((m) =>
        m.children?.some((c) => c.to && location.pathname.startsWith(c.to))
      )
      .map((m) => m.id);
    if (ids.length) {
      setExpandedModules((prev) => {
        const next    = new Set(prev);
        let   changed = false;
        ids.forEach((id) => {
          if (!next.has(id)) { next.add(id); changed = true; }
        });
        return changed ? next : prev;
      });
    }
  }, [modules, location.pathname]);

  React.useEffect(() => {
    initializeActiveModules();
  }, [initializeActiveModules]);

  const toggleModule = React.useCallback((id: number) => {
    setExpandingModule(id);
    const t = setTimeout(() => setExpandingModule(null), 600);
    setExpandedModules((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(
        "drawer-expanded-modules",
        JSON.stringify(Array.from(next))
      );
      return next;
    });
    return () => clearTimeout(t);
  }, []);

  const isModuleExpanded = React.useCallback(
    (id: number) => expandedModules.has(id),
    [expandedModules]
  );

  const contextValue = React.useMemo(
    () => ({ expandedModules, toggleModule, isModuleExpanded, expandingModule }),
    [expandedModules, toggleModule, isModuleExpanded, expandingModule]
  );

  const handleNavigate = React.useCallback(() => {
    navigate("/dashboard");
    handleClose();
  }, [navigate, handleClose]);

  const listHeight = `calc(100vh - ${Number(width === "xs" ? 130 : NAVBAR_HEIGHT) + 2}px - env(safe-area-inset-top, 0px))`;

  const BodyDrawer = React.useMemo(
    () => (
      <>
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-header-actions">
            <ButtonAntd
              className="drawer-close-btn"
              onClick={handleClose}
              icon={
                theme.direction === "ltr"
                  ? <ChevronLeftIcon style={{ fontSize: 18 }} />
                  : <ChevronRightIcon style={{ fontSize: 18 }} />
              }
              size="small"
            />
            <Tooltip
              title="Dashboard"
              placement="right"
              arrow
              componentsProps={propsTooltip}
            >
              <ButtonAntd
                className="drawer-home-btn"
                onClick={handleNavigate}
                icon={<HomeOutlined style={{ fontSize: 15 }} />}
                size="small"
              />
            </Tooltip>
          </div>
        </div>

        <Divider className="drawer-divider" />

        {/* Lista */}
        <List
          className="drawer-list"
          sx={{
            width:     width === "xs" ? drawerWidth : "auto",
            height:    listHeight,
            minHeight: listHeight,
            maxHeight: listHeight,
          }}
        >
          <DrawerStateContext.Provider value={contextValue}>
            {visibleModules.map((el) => (
              <DrawerItem key={`module_${el.id}`} data={el} open={open} />
            ))}
          </DrawerStateContext.Provider>
        </List>
      </>
    ),
    [
      visibleModules, open, width, theme.direction,
      contextValue, handleClose, handleNavigate, listHeight,
    ]
  );

  if (width === "xs")
    return (
      <MuiDrawer open={open} onClose={handleClose}>
        {BodyDrawer}
      </MuiDrawer>
    );

  return (
    <DrawerEl variant="permanent" open={open}>
      {BodyDrawer}
    </DrawerEl>
  );
};

/* ── DrawerItem — módulo con hijos ───────────────────────────────────────── */
export const DrawerItem = React.memo<PropsI>(
  ({ data, open }) => {
    const {
      id, name, nameSort, icon, iconLibrary,
      iconActive, iconActiveLibrary, children,
    } = data;

    const location  = useLocation();
    const { toggleModule, isModuleExpanded } = React.useContext(DrawerStateContext);

    const openOption    = isModuleExpanded(id);
    const isChildActive = React.useMemo(
      () => children?.some((c) => c.to && location.pathname.startsWith(c.to)) ?? false,
      [children, location.pathname]
    );
    const onClickOption = React.useCallback(() => toggleModule(id), [toggleModule, id]);

    /* módulo SIN hijos */
    if (!children?.length) {
      const isActive   = data.to ? location.pathname.startsWith(data.to) : false;
      const stateClass = isActive ? "state-active" : "";

      return (
        <ListItem disablePadding className={`drawer-module-item no-children ${stateClass}`}>
          <NavLink to={data.to ?? "/"} style={{ width: "100%", textDecoration: "none" }}>
            {({ isActive: navActive }) => (
              <ListItemButton
                className="drawer-module-btn"
                selected={navActive}
                sx={{ "&.Mui-selected": { background: "transparent !important" } }}
              >
                {/* Ícono — sin fondo cuando cerrado */}
                <ListItemIcon sx={{ minWidth: "unset" }}>
                  <Zoom in timeout={260}>
                    <div className={`drawer-module-icon${open ? " icon-open" : " icon-closed"}`}>
                      <DynamicIcon
                        icon={navActive ? iconActive : icon}
                        lib={navActive ? iconActiveLibrary : iconLibrary}
                        color={navActive ? "#ffffff" : COLOR_PRIMARY}
                        size={18}
                      />
                    </div>
                  </Zoom>
                </ListItemIcon>

                {open
                  ? <span className="drawer-module-text" style={{ fontSize: getFontSize(nameSort) }}>{nameSort}</span>
                  : <span className="drawer-mini-label">{nameSort}</span>
                }
              </ListItemButton>
            )}
          </NavLink>
        </ListItem>
      );
    }

    const stateClass = isChildActive
      ? "state-child-active"
      : openOption
        ? "state-expanded"
        : "state-default";

    return (
      <>
        <ListItem disablePadding className={`drawer-module-item ${stateClass}`}>
          <ListItemButton className="drawer-module-btn" onClick={onClickOption}>

            {/* Ícono — sin fondo cuando cerrado */}
            <ListItemIcon sx={{ minWidth: "unset" }}>
              <Zoom in timeout={260}>
                <div className={`drawer-module-icon${open ? " icon-open" : " icon-closed"}`}>
                  <DynamicIcon
                    icon={openOption || isChildActive ? iconActive : icon}
                    lib={openOption || isChildActive ? iconActiveLibrary : iconLibrary}
                    color={openOption || isChildActive ? "#ffffff" : COLOR_PRIMARY}
                    size={18}
                  />
                </div>
              </Zoom>
            </ListItemIcon>

            {open
              ? <span className="drawer-module-text" style={{ fontSize: getFontSize(nameSort) }}>{nameSort}</span>
              : <span className="drawer-mini-label">{nameSort}</span>
            }

            {open && (
              <div className={`drawer-expand-icon${openOption ? " is-open" : ""}`}>
                <ExpandMore sx={{ fontSize: 16, color: "inherit" }} />
              </div>
            )}
          </ListItemButton>
        </ListItem>

        {/* Hijos */}
        <Collapse
          in={openOption}
          timeout={{ enter: 360, exit: 240 }}
          easing={{
            enter: "cubic-bezier(0.4, 0, 0.2, 1)",
            exit:  "cubic-bezier(0.4, 0, 0.6, 1)",
          }}
          unmountOnExit
        >
          <List component="div" disablePadding className="drawer-children-panel">
            {children.map((el, index) => (
              <Fade
                key={`${el.id}_${el.code}`}
                in={openOption}
                timeout={240 + index * 60}
                style={{ transitionDelay: `${index * 30}ms` }}
              >
                <div className={`drawer-child-delay-${Math.min(index + 1, 6)}`}>
                  <DrawerItemChildren data={el} padding={0.5} open={open} />
                </div>
              </Fade>
            ))}
          </List>
        </Collapse>

        <Divider className="drawer-divider" />
      </>
    );
  },
  (prev, next) =>
    prev.data.id === next.data.id &&
    prev.open    === next.open &&
    JSON.stringify(prev.data.children) === JSON.stringify(next.data.children)
);

/* ── DrawerItemChildren — ítem hijo ──────────────────────────────────────── */
export const DrawerItemChildren = React.memo<PropsI>(
  ({ data, padding, open }) => {
    const {
      id, to, name, nameSort, icon, iconLibrary,
      iconActive, iconActiveLibrary, options, parentModuleId,
    } = data;

    return (
      <ListItem
        disablePadding
        className={`drawer-child-item${parentModuleId && options ? " has-options" : ""}`}
        sx={{ pl: padding }}
      >
        <NavLink to={to!} style={{ width: "100%", textDecoration: "none" }}>
          {({ isActive }) => (
            /* ── aplicamos child-active directamente al wrapper ── */
            <div className={isActive ? "navlinkA" : "navlink"}>
              <ListItem
                disablePadding
                /* child-active en el ListItem para que los selectores CSS funcionen */
                className={`drawer-child-item-inner${isActive ? " child-active" : ""}${parentModuleId && options ? " has-options" : ""}`}
              >
                <ListItemButton
                  className={`drawer-child-btn${!open ? " collapsed-mode" : ""}`}
                  selected={isActive}
                  sx={{ "&.Mui-selected": { background: "transparent !important" } }}
                >
                  {/* Ícono hijo — child-active en el div para que CSS pinte el fondo */}
                  <ListItemIcon sx={{ minWidth: "unset" }}>
                    <Zoom in timeout={200}>
                      <div className={`drawer-child-icon${isActive ? " child-active" : ""}`}>
                        <DynamicIcon
                          icon={isActive ? iconActive : icon}
                          lib={isActive ? iconActiveLibrary : iconLibrary}
                          color={isActive ? COLOR_TERTIARY : COLOR_PRIMARY}
                          size={isActive ? 20 : 17}
                        />
                      </div>
                    </Zoom>
                  </ListItemIcon>

                  {/* Texto abierto */}
                  {open && (
                    <span
                      className={`drawer-child-text${isActive ? " child-active" : ""}`}
                      style={{ fontSize: getFontSize(nameSort) }}
                    >
                      {nameSort}
                    </span>
                  )}

                  {/* Mini label colapsado */}
                  {!open && (
                    <span
                      className="drawer-mini-label"
                      style={{
                        color:      isActive ? COLOR_TERTIARY: COLOR_PRIMARY,
                        fontWeight: isActive ? 800 : 600,
                        position:   "static",
                      }}
                    >
                      {nameSort}
                    </span>
                  )}
                </ListItemButton>
              </ListItem>
            </div>
          )}
        </NavLink>
      </ListItem>
    );
  },
  (prev, next) =>
    prev.data.id  === next.data.id &&
    prev.open     === next.open &&
    prev.padding  === next.padding
);

/* ── Tamaño de fuente ─────────────────────────────────────────────────────── */
const getFontSize = (text: string): number => {
  const len = text.length;
  if (len <= 10) return 12;
  if (len <= 14) return 11;
  if (len <= 18) return 10;
  return 9;
};